import { Icon } from '@iconify/react';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { ControlledTextField } from '../../../../shared/components';

const SELECT_ALL_OPTION = '__SELECT_ALL__';

export default function UpperFloorAssignmentRow({
  label,
  placeholder = 'Select floor number',
  totalFloors,
  value = [],
  disabledFloors = [],
  onChange,
  disabled = false,
  index,
  canDelete = false,
  remove,
}: { label?: any; placeholder?: string; totalFloors: any; value?: any[]; disabledFloors?: any[]; onChange: any; disabled?: boolean; index: any; canDelete?: boolean; remove?: any }) {
  const floorOptions = Array.from({ length: totalFloors }, (_, i) => i + 1);

  const allSelected = floorOptions.length > 0 && value.length === floorOptions.length;

  const options = floorOptions.length ? [SELECT_ALL_OPTION, ...floorOptions] : [];

  const handleChange = (_, newValue) => {
    if (newValue.includes(SELECT_ALL_OPTION)) {
      onChange(allSelected ? [] : [...floorOptions]);
      return;
    }

    onChange([...newValue].sort((a, b) => a - b));
  };

  const count = value.length;
  const summary = count > 0 ? `${count} ${count === 1 ? 'floor' : 'floors'} selected` : '';

  useEffect(() => {
    const filtered = value.filter((v) => v <= totalFloors);
    if (filtered.length !== value.length) {
      onChange(filtered);
    }
  }, [onChange, totalFloors, value]);

  return (
    <Stack direction='row' gap={3} alignItems='center' sx={{ pl: 2 }}>
      <ControlledTextField
        name={`upperFloorGroups.${index}.name`}
        placeholder='Use of floor'
        rules={{ required: 'Please enter the use of floor' }}
        sx={{ width: 240 }}
        wrapperSx={{ width: 'auto' }}
      />
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={options}
        value={value}
        disabled={disabled || totalFloors === 0}
        onChange={handleChange}
        renderTags={() => null}
        getOptionLabel={(option) =>
          option === SELECT_ALL_OPTION ? 'Select all' : `Floor ${option}`
        }
        isOptionEqualToValue={(opt, val) => opt === val}
        getOptionDisabled={(option) =>
          option !== SELECT_ALL_OPTION && disabledFloors.includes(option)
        }
        renderOption={(props, option, { selected }) => {
          if (option === SELECT_ALL_OPTION) {
            return (
              <li {...props}>
                <Checkbox size='small' checked={allSelected} />
                Select all
              </li>
            );
          }

          return (
            <li {...props}>
              <Checkbox
                size='small'
                checked={selected}
                disabled={disabledFloors.includes(option)}
              />
              Floor {option}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size='small'
            placeholder={count === 0 ? placeholder : ''}
            InputProps={{
              ...params.InputProps,
              startAdornment: summary ? (
                <Typography variant='body2' sx={{ ml: 0.5, whiteSpace: 'nowrap' }}>
                  {summary}
                </Typography>
              ) : null,
            }}
          />
        )}
        sx={{ width: 240 }}
      />
      <IconButton onClick={() => remove && remove(index)} disabled={!canDelete} disableRipple>
        <Icon icon='ic:outline-delete' />
      </IconButton>
    </Stack>
  );
}
